import React, { useState } from 'react';
import { MdTune, MdExpandMore, MdExpandLess } from 'react-icons/md';
import MetricCard from '../../components/shared/MetricCard';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import ScannerTable from './components/ScannerTable';
import SymbolSearchAdd from './components/SymbolSearchAdd';

const SIGNAL_PILLS = [
  { value: '', label: 'All' },
  { value: 'BUY', label: 'Buy' },
  { value: 'WEAK_BUY', label: 'Lean Buy' },
  { value: 'HOLD', label: 'Neutral' },
  { value: 'WEAK_SELL', label: 'Lean Sell' },
  { value: 'SELL', label: 'Sell' },
];

const ScannerPage = ({ data }) => {
  const { meta, scanner, filters, loading, error, updateFilter } = data;
  const dist = meta?.distribution || {};
  const total = meta?.total_symbols || 0;
  const rows = scanner?.results || [];
  const [filtersOpen, setFiltersOpen] = useState(false);

  const universeLabel = filters.is_etf === 'false' ? 'Stocks' : filters.is_etf === 'true' ? 'ETFs' : 'Stocks + ETFs';
  const indexLabel = filters.index === 'sp500' ? 'S&P 500'
    : filters.index === 'nasdaq100' ? 'Nasdaq 100'
    : filters.index === 'dow30' ? 'Dow 30'
    : '';
  const activeFilterSummary = [
    filters.signal && SIGNAL_PILLS.find(p => p.value === filters.signal)?.label,
    universeLabel,
    indexLabel,
    filters.sector,
    filters.exchange,
  ].filter(Boolean).join(' · ');

  return (
    <div>
      <SymbolSearchAdd className="mb-4" />

      {/* Metric tiles — 2x2 mobile (drop 'As of'), 1x5 desktop */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3 mb-4">
        <MetricCard label="Universe" value={total} subtitle="scanned" />
        <MetricCard label="Buy + Lean" value={(dist.BUY || 0) + (dist.WEAK_BUY || 0)} valueColor="text-green-600 dark:text-green-400" subtitle={`${dist.BUY || 0} strong`} />
        <MetricCard label="Neutral" value={dist.HOLD || 0} subtitle="no edge" />
        <MetricCard label="Sell + Lean" value={(dist.SELL || 0) + (dist.WEAK_SELL || 0)} valueColor="text-red-500 dark:text-red-400" subtitle={`${dist.SELL || 0} strong`} />
        <div className="hidden md:block">
          <MetricCard label="As of" value={meta?.as_of_date || '—'} subtitle="latest snapshot" />
        </div>
      </div>

      {/* Filter expander */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg mb-4">
        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2 text-xs"
        >
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 min-w-0">
            <MdTune className="shrink-0" />
            <span className="uppercase tracking-wider text-[10px] shrink-0">Filters</span>
            <span className="text-[10px] text-gray-400 truncate">· {activeFilterSummary}</span>
          </div>
          {filtersOpen ? <MdExpandLess className="text-gray-400 shrink-0" /> : <MdExpandMore className="text-gray-400 shrink-0" />}
        </button>
        {filtersOpen && (
          <div className="px-3 pb-3 border-t border-gray-100 dark:border-gray-800 pt-3">
            <div className="flex flex-wrap items-center gap-2 mb-2 overflow-x-auto scrollbar-thin">
              {SIGNAL_PILLS.map((p) => {
                const active = filters.signal === p.value;
                return (
                  <button
                    key={p.value || 'all'}
                    onClick={() => updateFilter({ signal: p.value })}
                    className={`text-[11px] px-3 py-1 rounded-full whitespace-nowrap transition-colors ${
                      active
                        ? 'bg-[#c9a96e] text-white border border-[#c9a96e]'
                        : 'bg-transparent border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <input
                type="text"
                placeholder="Search ticker…"
                value={filters.search}
                onChange={(e) => updateFilter({ search: e.target.value })}
                className="text-xs px-2 py-1.5 bg-transparent border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100"
              />
              <select
                value={filters.is_etf}
                onChange={(e) => updateFilter({ is_etf: e.target.value })}
                className="text-xs px-2 py-1.5 bg-transparent border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100"
              >
                <option value="false">Stocks only</option>
                <option value="true">ETFs only</option>
                <option value="">Stocks + ETFs</option>
              </select>
              <select
                value={filters.index}
                onChange={(e) => updateFilter({ index: e.target.value })}
                className="text-xs px-2 py-1.5 bg-transparent border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100"
                title="Index membership"
              >
                <option value="">All indices</option>
                <option value="sp500">S&P 500{meta?.index_counts?.sp500 ? ` (${meta.index_counts.sp500})` : ''}</option>
                <option value="nasdaq100">Nasdaq 100{meta?.index_counts?.nasdaq100 ? ` (${meta.index_counts.nasdaq100})` : ''}</option>
                <option value="dow30">Dow 30{meta?.index_counts?.dow30 ? ` (${meta.index_counts.dow30})` : ''}</option>
              </select>
              <select
                value={filters.sector}
                onChange={(e) => updateFilter({ sector: e.target.value })}
                className="text-xs px-2 py-1.5 bg-transparent border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100"
              >
                <option value="">All sectors</option>
                {(meta?.sectors || []).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <select
                value={filters.exchange}
                onChange={(e) => updateFilter({ exchange: e.target.value })}
                className="text-xs px-2 py-1.5 bg-transparent border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100"
              >
                <option value="">All exchanges</option>
                {(meta?.exchanges || []).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {error && <div className="text-xs text-red-500 mb-2">{error}</div>}
      {loading && <LoadingSpinner message="Loading scanner…" />}
      {!loading && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-2">
          <ScannerTable
            rows={rows}
            sort={filters.sort}
            onSortChange={(sort) => updateFilter({ sort })}
          />
          {scanner && (scanner.next || scanner.previous) && (
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 px-2 pt-3">
              <button
                disabled={!scanner.previous}
                onClick={() => updateFilter({ page: Math.max(1, (filters.page || 1) - 1) })}
                className="px-3 py-1 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-30 hover:text-[#c9a96e]"
              >
                ← Prev
              </button>
              <span>Page {filters.page || 1} · {scanner.count} total</span>
              <button
                disabled={!scanner.next}
                onClick={() => updateFilter({ page: (filters.page || 1) + 1 })}
                className="px-3 py-1 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-30 hover:text-[#c9a96e]"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScannerPage;
